import { APIConfiguration } from '@savgroup-front-common/configuration';
import { CLIENT_COMMON_ERROR } from '@savgroup-front-common/constants';

import { Config } from './apiCall.types';
import { publishLatencySample } from './networkLatencyObserver';

export interface APIClientParams {
  url: string;
  config: Config;
  currentLanguage: string;
  authToken?: string | null;
}

interface FetchError {
  name?: string;
  message?: string;
}

interface FetchErrorDetails {
  isAborted: boolean;
  errors: FetchError[];
}

function getFetchErrorDetails(err: FetchError): FetchErrorDetails {
  if (err.name === 'AbortError') {
    return {
      isAborted: true,
      errors: [{ name: CLIENT_COMMON_ERROR.REQUEST_ABORTED }],
    };
  }

  if (err.message === 'Failed to fetch') {
    return {
      isAborted: false,
      errors: [{ name: CLIENT_COMMON_ERROR.FAILED_TO_FETCH }],
    };
  }

  return {
    isAborted: false,
    errors: [{ message: err.message }],
  };
}

function getNow(): number {
  if (typeof performance !== 'undefined' && performance.now) {
    return performance.now();
  }

  return Date.now();
}

type FinalConfig = Omit<Config, 'headers'> & {
  headers: Headers;
};

interface AbortState {
  isTimeout: boolean;
}

async function fetchWithTimeout(
  resource: string,
  options: FinalConfig,
  abortState: AbortState,
): Promise<Response> {
  const { signal: callerSignal, timeout, ...restOptions } = options;
  const controller = new AbortController();
  const onCallerAbort = (): void => controller.abort();

  if (callerSignal) {
    callerSignal.addEventListener('abort', onCallerAbort);

    if (callerSignal.aborted) {
      controller.abort();
    }
  }

  const id = globalThis.setTimeout(() => {
    abortState.isTimeout = true;
    controller.abort();
  }, timeout);

  try {
    return await fetch(resource, {
      ...restOptions,
      signal: controller.signal,
    });
  } finally {
    clearTimeout(id);

    if (callerSignal) {
      callerSignal.removeEventListener('abort', onCallerAbort);
    }
  }
}

function buildHeaders({
  config,
  currentLanguage,
  authToken,
  url,
}: {
  config: Config;
  currentLanguage: string;
  authToken?: string | null;
  url: string;
}): Headers {
  const headers = new Headers();

  headers.append('Accept', 'application/json');
  headers.append('Access-Control-Max-Age', '600');

  if (!(config.body instanceof FormData)) {
    headers.append('Content-Type', 'application/json');
  }

  headers.append('Accept-Language', currentLanguage);

  if (authToken != null && !url.includes(APIConfiguration.catalogCDN)) {
    headers.append('Authorization', `Bearer ${authToken}`);
  }

  Object.entries(config.headers ?? {}).forEach(([name, value]) => {
    headers.set(name, value);
  });

  return headers;
}

function resolveBody(config: Config): BodyInit | undefined {
  if (config.json !== null && config.json !== undefined) {
    return JSON.stringify(config.json);
  }

  return config.body;
}

function APIClient<T = unknown>({
  url,
  config,
  currentLanguage,
  authToken,
}: APIClientParams): Promise<T> {
  return new Promise((resolve, reject) => {
    const finalConfig: FinalConfig = {
      ...config,
      headers: buildHeaders({ config, currentLanguage, authToken, url }),
      body: resolveBody(config),
    };

    if (finalConfig.signal?.aborted) {
      reject({
        failure: true,
        isAborted: true,
        errors: [{ name: CLIENT_COMMON_ERROR.REQUEST_ABORTED }],
      });

      return;
    }

    const abortState: AbortState = { isTimeout: false };
    const fetchMethod = finalConfig.timeout
      ? (resource: string, options: FinalConfig): Promise<Response> =>
          fetchWithTimeout(resource, options, abortState)
      : fetch;

    const startTime = getNow();

    fetchMethod(url, finalConfig)
      .then(async (response) => {
        publishLatencySample({
          durationMs: getNow() - startTime,
          isTimeout: false,
          url,
        });

        const contentType = response.headers.get('content-type');
        const isSerialisable = Boolean(
          contentType?.includes('application/json'),
        );

        if (response.status === 200 || response.status === 201) {
          if (isSerialisable) {
            const result = await response.json();

            resolve(result);

            return;
          }

          if (config.responseType !== 'blob') {
            resolve((await response.text()) as T);

            return;
          }

          const file = (await response.blob()) as Blob & { name?: string };
          const fileNameIndexOf = contentType?.indexOf('filename=') ?? -1;
          const fileName =
            fileNameIndexOf >= 0
              ? contentType?.slice(fileNameIndexOf + 9)
              : undefined;

          if (!file.name) {
            file.name = fileName;
          }

          resolve(file as T);

          return;
        }

        if (isSerialisable) {
          const err = await response.json();

          reject({ ...err, statusCode: response.status });

          return;
        }

        reject({
          failure: true,
          errors: [{ message: response.statusText }],
          statusCode: response.status,
        });
      })
      .catch((err: FetchError) => {
        const { isAborted, errors } = getFetchErrorDetails(err);

        publishLatencySample({
          durationMs: getNow() - startTime,
          isTimeout: abortState.isTimeout,
          url,
        });

        reject({
          failure: true,
          isAborted,
          errors,
        });
      });
  });
}

export default APIClient;
